import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView, useScroll, useTransform } from "framer-motion";
import { useSite, UI, resolveImg } from "@/lib/site";
import { MaskLine, Reveal } from "@/components/Reveal";
import { SocialButtons } from "@/components/WaButton";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const target = parseInt(value, 10);
  const [n, setN] = useState(Number.isNaN(target) ? value : target);
  useEffect(() => {
    if (!inView || Number.isNaN(target)) return undefined;
    const c = animate(0, target, { duration: 2, ease: [0.22, 1, 0.36, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, target]);
  return <span ref={ref}>{n}{suffix}</span>;
};

const Badge = () => (
  <motion.svg viewBox="0 0 200 200" className="h-28 w-28 md:h-36 md:w-36" animate={{ rotate: 360 }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }} aria-hidden="true">
    <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
    <circle cx="100" cy="100" r="98" fill="#1d1712" />
    <text fill="#f0d78e" fontSize="12.5" letterSpacing="3.2" fontFamily="Manrope" fontWeight="600">
      <textPath href="#circ">DARI EVENT · 17 YEARS · 500+ EVENTS ·</textPath>
    </text>
    <text x="100" y="112" textAnchor="middle" fill="#f0d78e" fontSize="34" fontFamily="Cormorant Garamond" fontStyle="italic">D</text>
  </motion.svg>
);

export const About = () => {
  const { t, content } = useSite();
  const a = content?.about || {};
  const f = content?.founder || {};
  const stats = Array.isArray(content?.stats) ? content.stats : [];
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" data-testid="about-section" className="relative bg-[#fbf7ef] px-5 py-24 md:px-10 md:py-36">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="relative lg:col-span-5" ref={imgRef}>
            <Reveal className="relative mx-auto max-w-[460px]">
              <div className="absolute inset-0 translate-x-4 translate-y-4 arch border border-[#b8893b]/50" />
              <div className="arch relative aspect-[4/5] overflow-hidden bg-[#e9dcc5]">
                <motion.img style={{ y, scale: 1.18 }} src={resolveImg(f.image)} alt={t(f.name)} className="absolute inset-0 h-full w-full object-cover object-top" data-testid="founder-photo" />
              </div>
              <div className="absolute -right-2 -top-6 sm:-right-8"><Badge /></div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <p className="eyebrow">{t(a.eyebrow)}</p>
            <h2 className="font-display mt-6 text-4xl font-medium leading-[1.02] text-[#1d1712] sm:text-5xl lg:text-6xl" data-testid="about-title">
              <MaskLine inView>{t(a.title)}</MaskLine>
            </h2>
            <Reveal delay={0.1}>
              <p className="font-display mt-8 text-2xl italic leading-snug text-[#5c3f17] md:text-3xl">{t(a.lead)}</p>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4a3f34]">{t(a.text)}</p>
            </Reveal>

            <Reveal delay={0.15} className="mt-12 rounded-[26px] border border-[#b8893b]/25 bg-[#f3eadb]/70 p-7 md:p-10" data-testid="founder-card">
              <p className="eyebrow">{t(f.eyebrow)}</p>
              <h3 className="font-display mt-3 text-4xl font-medium text-[#1d1712] md:text-5xl" data-testid="founder-name">{t(f.name)}</h3>
              <p className="mt-1 text-sm font-medium uppercase tracking-[0.15em] text-[#8a6424]">{t(f.role)}</p>
              <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                {(Array.isArray(f.regalia) ? f.regalia : []).map((r, i) => (
                  <li key={i} className="flex gap-3 text-[0.95rem] leading-snug text-[#2a2119]" data-testid={`founder-regalia-${i}`}>
                    <span className="mt-1.5 h-2 w-2 shrink-0 rotate-45 bg-gradient-to-br from-[#f0d78e] to-[#8a6424]" />
                    {t(r)}
                  </li>
                ))}
              </ul>
              <blockquote className="font-display mt-8 border-l border-[#b8893b] pl-5 text-xl italic text-[#5c3f17]">{t(f.quote)}</blockquote>
              <SocialButtons label={t(UI.contact)} className="mt-8" testPrefix="founder" />
            </Reveal>
          </div>
        </div>

        <div className="mt-28 grid grid-cols-2 border-t border-[#b8893b]/25 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className={`border-b border-[#b8893b]/25 py-8 pr-4 lg:border-b-0 ${i % 2 === 0 ? "" : "pl-6"} lg:pl-6 ${i > 0 ? "lg:border-l" : "lg:pl-0"}`} data-testid={`stat-${i}`}>
              <p className="font-display gold-text text-5xl font-medium md:text-7xl"><Counter value={s.value} suffix={s.suffix} /></p>
              <p className="mt-2 text-sm text-[#5b4f43]">{t(s.label)}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
